import { Trans, useTranslation } from "react-i18next";
import { useMemo, useState } from "react";
import type { DashboardNewsItem } from "@arc/contracts";
import { ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ManualIcon } from "@/components/icons";
import { Disclosure } from "@/components/ui/disclosure";
import { cn } from "@/lib/utils";
import type { AuthSession } from "../../../auth/keycloak.js";
import { formatDateTime } from "../../../lib/date.js";
import { useFacilityNavParams } from "../../facilities/drilldown/useFacilityNavParams.js";
import { NEWS_STATUS_KEY } from "../OverviewPage.js";
import { useDashboardNews } from "../useDashboardNews.js";
import { bucketNews, NEWS_BUCKET_LABEL_KEY } from "./news-buckets.js";

/**
 * The legacy Verlauf page (ADR-034 D4): the status changes over the caller's visible manuals in the legacy's five
 * time buckets (empty ones not rendered), "Load more" for the next keyset page, and the selected entry's detail —
 * the sentence, date and time, who did it, the report text of a mark, Open manual / Open facility.
 */
export function HistoryPage({ session }: { session: AuthSession }) {
  const { t } = useTranslation();
  const nav = useFacilityNavParams();
  const news = useDashboardNews(session);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const items = useMemo(() => news.data?.pages.flatMap((p) => p.items) ?? [], [news.data]);
  const now = useMemo(() => new Date(), [items]); // the buckets move with the data, not with every render
  const buckets = useMemo(() => bucketNews(items, now), [items, now]);
  const selected = items.find((n) => n.id === selectedId) ?? null;

  return (
    <div className="flex flex-col gap-4">
      {news.isPending ? (
        <p role="status" className="text-muted-foreground text-sm">
          {t("other.loading")}
        </p>
      ) : news.isError ? (
        <div className="flex flex-col items-start gap-2">
          <p role="alert" className="text-destructive text-sm">
            {t("dashboard.couldntLoadTheHistory")}
          </p>
          <Button variant="outline" size="sm" onPress={() => void news.refetch()}>
            {t("dashboard.tryAgain")}
          </Button>
        </div>
      ) : items.length === 0 ? (
        <p className="text-muted-foreground text-sm">{t("dashboard.noNewsYet")}</p>
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,26rem)]">
          <div className="flex flex-col gap-2">
            {buckets.map((b) => (
              <Disclosure key={b.bucket} defaultExpanded title={<span>{t(NEWS_BUCKET_LABEL_KEY[b.bucket])}</span>}>
                <ul aria-label={t(NEWS_BUCKET_LABEL_KEY[b.bucket])} className="m-0 flex list-none flex-col gap-1 p-0">
                  {b.items.map((n) => (
                    <li key={n.id}>
                      <button
                        type="button"
                        aria-pressed={n.id === selectedId}
                        onClick={() => setSelectedId(n.id === selectedId ? null : n.id)}
                        className={cn(
                          "hover:bg-muted focus-visible:ring-ring/50 flex w-full cursor-pointer flex-col gap-0.5 rounded-md border-0 bg-transparent px-2 py-1.5 text-left text-sm outline-none focus-visible:ring-2",
                          n.id === selectedId && "bg-muted",
                        )}
                      >
                        <NewsSentence n={n} />
                        <span className="text-muted-foreground text-xs">{formatDateTime(n.createdAt)}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </Disclosure>
            ))}
            {news.hasNextPage ? (
              <div>
                <Button variant="outline" size="sm" isDisabled={news.isFetchingNextPage} onPress={() => void news.fetchNextPage()}>
                  {news.isFetchingNextPage ? t("other.loading") : t("dashboard.loadMore")}
                </Button>
              </div>
            ) : null}
          </div>
          <section aria-label={t("dashboard.entryDetails")} className="border-border bg-muted/30 flex flex-col gap-3 rounded-lg border p-4 text-sm">
            {selected === null ? (
              <p className="text-muted-foreground m-0">{t("dashboard.noManualSelectedTextHistory")}</p>
            ) : (
              <>
                <p className="m-0">
                  <NewsSentence n={selected} />
                </p>
                <dl className="m-0 grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1">
                  <dt className="text-muted-foreground">{t("dashboard.dateAndTime")}</dt>
                  <dd className="m-0">{formatDateTime(selected.createdAt)}</dd>
                  <dt className="text-muted-foreground">{t("dashboard.editedBy")}</dt>
                  <dd className="m-0">{selected.actorName ?? "—"}</dd>
                  {selected.comment ? (
                    <>
                      <dt className="text-muted-foreground">{t("other.report.reportText")}</dt>
                      <dd className="m-0 whitespace-pre-wrap">{selected.comment}</dd>
                    </>
                  ) : null}
                </dl>
                <div className="flex flex-wrap gap-2">
                  <Button variant="accent" className="h-8 rounded-[10px] px-3 text-[13px]" onPress={() => nav.openManualDetails(selected.facilityId, selected.manualId)}>
                    <ManualIcon aria-hidden="true" />
                    {t("dashboard.openManual")}
                  </Button>
                  <Button variant="outline" size="sm" onPress={() => nav.openManuals(selected.facilityId)}>
                    {t("facilitySelection.openFacility")}
                    <ChevronRight aria-hidden="true" />
                  </Button>
                </div>
              </>
            )}
          </section>
        </div>
      )}
    </div>
  );
}

function NewsSentence({ n }: { n: DashboardNewsItem }) {
  const { t } = useTranslation();
  // The three names are component CHILDREN, not interpolated values: `Trans` re-parses an interpolated string as
  // markup under `escapeValue: false`, so a facility called "A <b> B" would be swallowed. German also puts the
  // actor BEFORE the status, which is why the with-actor case is its own whole sentence rather than a suffix.
  const parts = {
    manual: <strong>{n.manualName}</strong>,
    facility: <strong>{n.facilityName}</strong>,
    status: <strong>{t(NEWS_STATUS_KEY[n.action])}</strong>,
  };
  return (
    <span>
      {n.actorName ? (
        <Trans i18nKey="dashboard.newsSentenceBy" components={parts} values={{ actor: n.actorName }} />
      ) : (
        <Trans i18nKey="dashboard.newsSentence" components={parts} />
      )}
      .
    </span>
  );
}
